import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { format as formatDate } from "date-fns"
import { isBefore, isSameDay, startOfDay } from "date-fns"
import { useSearchParams } from "react-router-dom"
import { toast } from "sonner"

import { StatBand } from "@/components/shared/StatBand"
import { useAuth } from "@/contexts/AuthContext"
import { useLocations, useRouteInstancesForDate, useShiftExceptions } from "@/hooks/useCollections"
import { useStableVariance } from "@/hooks/useStableVariance"
import { useSyncMetrics } from "@/hooks/useSyncMetrics"
import { describeVarianceBands, summariseAlarmCauses, summariseVariance } from "@/lib/statusDisplay"
import { coverBadge } from "@/lib/shiftCover"
import { buildHourMarkers, DEFAULT_HOUR_WIDTH, timelineWidthFor } from "@/lib/timelineScale"
import { downloadCsv } from "@/lib/csvExport"
import {
  filterMonitoringRoutes,
  sortMonitoringRoutes,
  standbyLast,
} from "@/lib/monitoringRouteOrder"
import { buildMonitoringRoutesCsv } from "@/lib/monitoringRoutesCsv"
import { Route, Stop } from "@/types/collections"

import { CancelRouteDialog } from "./RouteActions"
import RouteDetailsSheet from "./RouteDetailsSheet"
import StopDetailsPanel from "./StopDetailsPanel"
import ShiftExceptionsPanel from "../planning/ShiftExceptionsPanel"
import AdHocStopDialog from "./timeline/AdHocStopDialog"
import CancelStopDialog from "./timeline/CancelStopDialog"
import RouteLabelCell from "./timeline/RouteLabelCell"
import { GridLines, HourHeader } from "./timeline/TimelineGrid"
import TimelineRouteRow from "./timeline/TimelineRouteRow"
import TimelineToolbar from "./timeline/TimelineToolbar"
import UnsavedChangesBanner from "./timeline/UnsavedChangesBanner"
import CreateAdHocRouteDialog from "./timeline/CreateAdHocRouteDialog"
import DuplicateRouteDialog from "./timeline/DuplicateRouteDialog"
import SaveBeforeLockDialog from "./timeline/SaveBeforeLockDialog"
import { useAdHocStopForm } from "./timeline/useAdHocStopForm"
import { useDayStats, useRouteLoadFactors } from "./timeline/useDayStats"
import { useDragCreateStop } from "./timeline/useDragCreateStop"
import { useStagedRouteEdits } from "./timeline/useStagedRouteEdits"
import { useStopDrag } from "./timeline/useStopDrag"
import { useTimelineScroll } from "./timeline/useTimelineScroll"

const TimelineView: React.FC = () => {
  const { can } = useAuth()
  const [searchParams] = useSearchParams()
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    // Deep-link from Route Performance via ?date=YYYY-MM-DD.
    const paramDate = searchParams.get("date")
    if (paramDate) {
      const d = new Date(paramDate + "T12:00:00")
      if (!isNaN(d.getTime())) return d
    }
    return new Date()
  })
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState<string[]>([])
  const [depotFilter, setDepotFilter] = useState<string[]>([])
  const [sortBy, setSortBy] = useState<string>("none")
  const [bandFilter, setBandFilter] = useState<string | null>(null)
  const [selectedStop, setSelectedStop] = useState<Stop | null>(null)
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null)
  const [stopPanelOpen, setStopPanelOpen] = useState(false)
  const [routePanelOpen, setRoutePanelOpen] = useState(false)
  const [showShiftPanel, setShowShiftPanel] = useState(false)
  const [selectedRouteForPanel, setSelectedRouteForPanel] = useState<Route | null>(null)
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [hourWidth, setHourWidth] = useState(DEFAULT_HOUR_WIDTH)

  const dateStr = formatDate(selectedDate, "yyyy-MM-dd")
  const { data: routeInstancesData, refetch: refetchRoutes } = useRouteInstancesForDate(dateStr)

  // Deliberately not on the route rows; the cover panel is where cover is worked.
  const { data: shiftExceptions } = useShiftExceptions({ from: dateStr, to: dateStr })
  const shiftCoverBadge = coverBadge(shiftExceptions?.exceptions)
  const { data: locationsData } = useLocations()

  // Only today can be edited.
  const isToday = isSameDay(selectedDate, new Date())
  const isPastDate = isBefore(startOfDay(selectedDate), startOfDay(new Date()))

  const locations = locationsData?.data || []

  const staged = useStagedRouteEdits({
    routeInstancesData,
    locations,
    refetchRoutes,
    isToday,
    isPastDate,
    readOnly: !can.editRoutes,
  })
  const { routes, unsavedChanges, snapshotRoute, applyDraggedStops } = staged

  const [highlightedRouteId, setHighlightedRouteId] = useState<string | null>(() => {
    return searchParams.get("routeId") || null
  })

  // Handles navigation while MonitoringContent is already mounted.
  useEffect(() => {
    const paramDate = searchParams.get("date")
    if (paramDate) {
      const d = new Date(paramDate + "T12:00:00")
      if (!isNaN(d.getTime())) {
        setSelectedDate(d)
      }
    }
    const paramRouteId = searchParams.get("routeId")
    if (paramRouteId) {
      setHighlightedRouteId(paramRouteId)
    }
  }, [searchParams])

  const [deleteRouteDialogOpen, setDeleteRouteDialogOpen] = useState(false)
  const [routeToDelete, setRouteToDelete] = useState<Route | null>(null)
  const [deleteStopDialogOpen, setDeleteStopDialogOpen] = useState(false)
  const [stopToDelete, setStopToDelete] = useState<{ stop: Stop; route: Route } | null>(null)

  const [createAdHocRouteDialogOpen, setCreateAdHocRouteDialogOpen] = useState(false)
  const [duplicateRouteDialogOpen, setDuplicateRouteDialogOpen] = useState(false)

  const routeLoadFactors = useRouteLoadFactors(routes)

  // Cancelled routes are hidden unless selected in the Status filter. Mutations still operate on
  // the full `routes` array.
  const visibleRoutes = useMemo(() => {
    return statusFilter.includes("cancelled")
      ? routes
      : routes.filter((r) => r.status !== "cancelled")
  }, [routes, statusFilter])

  // Smoothed so a route on a threshold doesn't flip band each tick. Declared here because the alarm
  // filter must select the same routes as the banner.
  const stableVariance = useStableVariance(visibleRoutes, currentTime)
  const syncMetrics = useSyncMetrics(currentTime)

  const filteredRoutes = useMemo(
    () =>
      filterMonitoringRoutes(
        visibleRoutes,
        { searchTerm, statusFilter, depotFilter, bandFilter },
        { currentTime, varianceOf: stableVariance, loadFactors: routeLoadFactors }
      ),
    [
      visibleRoutes,
      searchTerm,
      statusFilter,
      depotFilter,
      bandFilter,
      stableVariance,
      currentTime,
      routeLoadFactors,
    ]
  )

  const sortedRoutes = useMemo(
    () => standbyLast(sortMonitoringRoutes(filteredRoutes, sortBy, currentTime, routeLoadFactors)),
    [filteredRoutes, sortBy, currentTime, routeLoadFactors]
  )

  /**
   * Label and track columns are aligned only by ROW_HEIGHT, so the stand-by divider is a border on
   * this row in both.
   */
  const firstStandbyRowIndex = useMemo(
    () => sortedRoutes.findIndex((route) => route.routeKind === "standby"),
    [sortedRoutes],
  )

  const {
    timelineHeaderRef,
    timelineContentRef,
    routeListRef,
    scrollbarGutter,
    handleTimelineScroll,
    scrollToRow,
  } = useTimelineScroll(selectedDate, hourWidth)

  const adHocStop = useAdHocStopForm({
    dateStr,
    routes,
    locations,
    routeInstancesData,
    isPastDate,
    nextPendingStopId: staged.nextPendingStopId,
    stageStopAdd: staged.stageStopAdd,
    onStaged: (route, arrivalTime) => {
      setTimeout(() => {
        scrollToRow(
          routes.findIndex((r) => r.id === route.id),
          arrivalTime
        )
      }, 100)
    },
  })

  const { stats, statChips } = useDayStats(visibleRoutes)

  /** Filters on the derived status, not the raw DB enum in statusFilter. */
  const handleBandFilter = useCallback(
    (filter: string) => {
      const next = bandFilter === filter ? null : filter
      setBandFilter(next)
      // The attention list is only useful worst-first.
      if (next === "alarm") setSortBy("concern")
      else if (sortBy === "concern") setSortBy("none")
    },
    [bandFilter, sortBy]
  )

  // Run state as a partition (segments) and things to act on (chips), kept apart so segments sum to
  // the total.

  const variance = useMemo(
    () => ({
      ...summariseVariance(visibleRoutes, currentTime, stableVariance),
      alarmCauses: summariseAlarmCauses(visibleRoutes, currentTime, {
        varianceOf: stableVariance,
        loadFactor: (r) => routeLoadFactors.get(r.id),
      }),
      breakdown: describeVarianceBands(visibleRoutes, currentTime, {
        varianceOf: stableVariance,
        loadFactor: (r) => routeLoadFactors.get(r.id),
      }),
    }),
    [visibleRoutes, currentTime, stableVariance, routeLoadFactors]
  )

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000)
    return () => clearInterval(interval)
  }, [])

  const { draggingStopId, isDragging, handleDragStart } = useStopDrag({
    routes,
    hourWidth,
    isToday,
    timelineContentRef,
    snapshotRoute,
    applyDraggedStops,
  })

  const { dragCreateState, handleDragCreateStart } = useDragCreateStop({
    sortedRoutes,
    isToday,
    selectedDate,
    hourWidth,
    timelineContentRef,
    onDrawn: adHocStop.openForDrawnStop,
  })

  const uniqueDepots = useMemo(() => {
    const depots = new Set(routes.map((route) => route.depot))
    return Array.from(depots).sort()
  }, [routes])

  // Read through a ref so a zoom or day change mid-highlight doesn't scroll again.
  const scrollToRowRef = useRef(scrollToRow)
  scrollToRowRef.current = scrollToRow

  useEffect(() => {
    if (!highlightedRouteId) return

    const route = routes.find((r) => r.id === highlightedRouteId)
    if (!route) return

    const scrollToRow = scrollToRowRef.current
    setTimeout(() => {
      scrollToRow(
        sortedRoutes.findIndex((r) => r.id === highlightedRouteId),
        route.plannedStartTime
      )

      setTimeout(() => {
        setHighlightedRouteId(null)
      }, 3000)
    }, 100)
  }, [highlightedRouteId, routes, sortedRoutes])

  const handleRouteClick = useCallback(
    (routeId: string, event?: React.MouseEvent) => {
      if (event) {
        const routeListElement = routeListRef.current
        if (routeListElement && !routeListElement.contains(event.target as Node)) {
          return
        }
      }

      // Look up by id to get the latest version.
      const currentRoute = routes.find((r) => r.id === routeId)
      if (currentRoute) {
        setSelectedRouteId(routeId)
        setSelectedRouteForPanel(currentRoute)
        setRoutePanelOpen(true)
      }
    },
    [routes, routeListRef]
  )

  // Keep the open panel's route current; don't open it automatically.
  useEffect(() => {
    if (selectedRouteId && routePanelOpen) {
      const currentRoute = routes.find((r) => r.id === selectedRouteId)
      if (currentRoute) {
        setSelectedRouteForPanel(currentRoute)
      } else {
        setRoutePanelOpen(false)
        setSelectedRouteId(null)
        setSelectedRouteForPanel(null)
      }
    } else if (!routePanelOpen && selectedRouteId) {
      setSelectedRouteId(null)
      setSelectedRouteForPanel(null)
    }
  }, [routes, selectedRouteId, routePanelOpen])

  const highlightNewRoute = (routeId: string) => {
    setHighlightedRouteId(routeId)
    setTimeout(() => setHighlightedRouteId(null), 3000)
  }

  const handleExport = useCallback(() => {
    if (!routes || routes.length === 0) {
      toast.error("No data to export", {
        description: "There are no routes to export for this date.",
      })
      return
    }

    const csvContent = buildMonitoringRoutesCsv(routes, dateStr)

    downloadCsv(`routes_${dateStr}.csv`, csvContent)

    toast.success("Export successful", {
      description: `Exported ${routes.length} route(s) to CSV.`,
    })
  }, [routes, dateStr])

  const handleStopClick = (stop: Stop, route: Route) => {
    // Unlocked routes are in edit mode, except on past dates.
    if (!route.isLocked && !isPastDate) return

    setSelectedStop(stop)
    setSelectedRoute(route)
    setStopPanelOpen(true)
  }

  const handleDeleteRoute = (route: Route, e?: React.MouseEvent) => {
    e?.stopPropagation()
    setRouteToDelete(route)
    setDeleteRouteDialogOpen(true)
  }

  const handleRouteCancelled = (cancelled: Route) => {
    staged.forgetUnsavedRoute(cancelled.id)
    refetchRoutes()
    setRouteToDelete(null)
  }

  const handleDeleteStop = (stop: Stop, route: Route) => {
    setStopToDelete({ stop, route })
    setDeleteStopDialogOpen(true)
  }

  const confirmDeleteStop = () => {
    if (!stopToDelete) return

    const { stop, route } = stopToDelete

    staged.stageStopRemoval(stop, route)

    if (selectedStop?.id === stop.id) {
      setStopPanelOpen(false)
      setSelectedStop(null)
    }

    toast.success("Stop cancelled", {
      description: `Stop "${stop.name}" will be removed from route ${route.code} when you save.`,
    })

    setDeleteStopDialogOpen(false)
    setStopToDelete(null)
  }

  const hourMarkers = buildHourMarkers(hourWidth)
  const timelineWidth = timelineWidthFor(hourWidth)

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-palette-jungleGreen000">
      <StatBand
        lede={`${stats.totalRoutes} ${stats.totalRoutes === 1 ? "route" : "routes"}`}
        variance={variance}
        onShowAlarms={() => handleBandFilter("alarm")}
        alarmsShown={bandFilter === "alarm"}
        chips={statChips}
        metrics={syncMetrics}
      />

      {unsavedChanges.size > 0 && !isPastDate && (
        <UnsavedChangesBanner
          routeCount={unsavedChanges.size}
          isSaving={staged.isSaving}
          onSaveAll={staged.handleGlobalSaveChanges}
          onDiscardAll={staged.handleGlobalDiscardChanges}
        />
      )}

      <div className="flex min-h-0 flex-1 flex-row">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <TimelineToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        depots={uniqueDepots}
        depotFilter={depotFilter}
        onDepotFilterChange={setDepotFilter}
        sortBy={sortBy}
        onSortChange={setSortBy}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        hourWidth={hourWidth}
        onHourWidthChange={setHourWidth}
        showShiftPanel={showShiftPanel}
        onToggleShiftPanel={() => setShowShiftPanel(!showShiftPanel)}
        shiftCoverBadge={shiftCoverBadge}
        isPastDate={isPastDate}
        canSeeShiftCover={can.shiftCover}
        canEditRoutes={can.editRoutes}
        onExport={handleExport}
        onCreateAdHocRoute={() => setCreateAdHocRouteDialogOpen(true)}
        onDuplicateRoute={() => setDuplicateRouteDialogOpen(true)}
      />

      <div className="flex flex-shrink-0 border-b border-palette-jungleGreen200 bg-palette-jungleGreen000">
        <div className="flex h-10 w-64 flex-shrink-0 items-center border-r border-palette-jungleGreen200 px-3">
          <span className="text-xs font-medium uppercase tracking-wider text-palette-jungleGreen500">
            Routes ({filteredRoutes.length})
          </span>
        </div>

        <div ref={timelineHeaderRef} className="h-10 flex-1 overflow-x-hidden">
          <HourHeader
            hourMarkers={hourMarkers}
            width={timelineWidth + scrollbarGutter.right}
          />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div
          ref={routeListRef}
          className="w-64 flex-shrink-0 overflow-hidden border-r border-palette-jungleGreen200 bg-white"
        >
          {sortedRoutes.map((route, rowIndex) => (
            <RouteLabelCell
              key={route.id}
              route={route}
              isFirstStandbyRow={rowIndex === firstStandbyRowIndex}
              hasUnsaved={unsavedChanges.has(route.id)}
              isHighlighted={highlightedRouteId === route.id}
              isToday={isToday}
              isPastDate={isPastDate}
              canEdit={can.editRoutes}
              currentTime={currentTime}
              loadFactor={routeLoadFactors.get(route.id)}
              rowVariance={stableVariance(route)}
              onSelect={handleRouteClick}
              onToggleLock={staged.toggleRouteLock}
              onCancelRoute={handleDeleteRoute}
            />
          ))}

          {/* Stands in for the tracks' horizontal scrollbar so both panes scroll equally far. */}
          {scrollbarGutter.bottom > 0 && (
            <div aria-hidden style={{ height: `${scrollbarGutter.bottom}px` }} />
          )}
        </div>

        <div
          ref={timelineContentRef}
          className="flex-1 overflow-auto"
          onScroll={handleTimelineScroll}
        >
          <div className="relative bg-white" style={{ width: `${timelineWidth}px` }}>
            <GridLines
              hourMarkers={hourMarkers}
              hourWidth={hourWidth}
              rowCount={filteredRoutes.length}
              selectedDate={selectedDate}
              currentTime={currentTime}
            />

            {sortedRoutes.map((route, rowIndex) => (
              <TimelineRouteRow
                key={route.id}
                route={route}
                isFirstStandbyRow={rowIndex === firstStandbyRowIndex}
                hasUnsaved={unsavedChanges.has(route.id)}
                isHighlighted={highlightedRouteId === route.id}
                isToday={isToday}
                isPastDate={isPastDate}
                currentTime={currentTime}
                selectedDate={selectedDate}
                hourWidth={hourWidth}
                trackVariance={stableVariance(route)}
                dragPreview={dragCreateState?.rowIndex === rowIndex ? dragCreateState : null}
                draggingStopId={draggingStopId}
                isDragging={isDragging}
                onDragCreateStart={(e) => handleDragCreateStart(e, rowIndex)}
                onStopDragStart={handleDragStart}
                onStopClick={handleStopClick}
                onStopDelete={handleDeleteStop}
              />
            ))}
          </div>
        </div>

      </div>
        </div>

        {can.shiftCover && (
          <ShiftExceptionsPanel
            isOpen={showShiftPanel}
            onClose={() => setShowShiftPanel(false)}
            selectedDate={selectedDate}
            onHighlightRoute={setHighlightedRouteId}
          />
        )}
      </div>

      <RouteDetailsSheet
        route={selectedRouteForPanel}
        open={routePanelOpen}
        onOpenChange={(open) => {
          setRoutePanelOpen(open)
          if (!open) {
            setSelectedRouteId(null)
            setSelectedRouteForPanel(null)
          }
        }}
        viewDate={selectedDate}
        onCancelled={() => {
          setRoutePanelOpen(false)
          setSelectedRouteId(null)
          setSelectedRouteForPanel(null)
          refetchRoutes()
        }}
        onDuplicated={(newRouteId) => {
          setRoutePanelOpen(false)
          setSelectedRouteId(null)
          setSelectedRouteForPanel(null)
          refetchRoutes()
          highlightNewRoute(newRouteId)
        }}
      />

      {/* After the route panel: same z-index, so DOM order puts the drill-down on top. */}
      <StopDetailsPanel
        stop={selectedStop}
        route={selectedRoute}
        open={stopPanelOpen}
        onOpenChange={setStopPanelOpen}
      />

      <SaveBeforeLockDialog {...staged.lockConfirmation} />

      <CreateAdHocRouteDialog
        open={createAdHocRouteDialogOpen}
        onOpenChange={setCreateAdHocRouteDialogOpen}
        selectedDate={selectedDate}
        onCreated={(routeId) => {
          refetchRoutes()
          if (routeId) highlightNewRoute(routeId)
        }}
      />

      <DuplicateRouteDialog
        open={duplicateRouteDialogOpen}
        onOpenChange={setDuplicateRouteDialogOpen}
        routes={sortedRoutes}
        selectedDate={selectedDate}
        onDuplicated={(routeId) => {
          refetchRoutes()
          highlightNewRoute(routeId)
        }}
      />

      <CancelRouteDialog
        route={routeToDelete}
        open={deleteRouteDialogOpen}
        onOpenChange={(open) => {
          setDeleteRouteDialogOpen(open)
          if (!open) setRouteToDelete(null)
        }}
        viewDate={selectedDate}
        onCancelled={handleRouteCancelled}
      />

      <CancelStopDialog
        open={deleteStopDialogOpen}
        onOpenChange={setDeleteStopDialogOpen}
        target={stopToDelete}
        onConfirm={confirmDeleteStop}
      />

      <AdHocStopDialog adHocStop={adHocStop} locations={locations} />
    </div>
  )
}

export default TimelineView
